import { useRef, useEffect } from 'react'

/**
 * AutoSizingTextarea — a textarea that grows with its content.
 *
 * No fixed height, no scrollbar, no drag-to-resize handle. The user
 * sees every line of what they're editing.
 *
 * Implementation: whenever `value` changes, we reset the element's
 * height to `auto` (lets it shrink when text is deleted) and then set
 * it to `scrollHeight`, which is the height required to fit the
 * current content.
 *
 * Accepts a `minHeight` prop (defaults 60px) so the initial render has
 * a sensible click target even when empty. Everything else is passed
 * straight through — this is a drop-in for <textarea>.
 */
export default function AutoSizingTextarea({
  value,
  onChange,
  style,
  minHeight = 60,
  ...rest
}) {
  const ref = useRef(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    // Reset so the scrollHeight reflects current content (not the
    // previous, possibly larger, size).
    el.style.height = 'auto'
    el.style.height = Math.max(el.scrollHeight, minHeight) + 'px'
  }, [value, minHeight])

  return (
    <textarea
      ref={ref}
      value={value ?? ''}
      onChange={onChange}
      style={{
        width: '100%',
        minHeight,
        padding: '8px 10px',
        border: '1px solid var(--accent)',
        borderRadius: 6,
        fontSize: 13,
        fontFamily: 'inherit',
        lineHeight: 1.55,
        // Disable manual resize; we handle sizing ourselves.
        resize: 'none',
        outline: 'none',
        boxSizing: 'border-box',
        overflow: 'hidden',
        background: '#fff',
        ...style,
      }}
      {...rest}
    />
  )
}
